import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Brain, Activity, Dumbbell, GitCompare } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1574629810360-7efbbe195018?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG2 = "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG3 = "https://images.unsplash.com/photo-1602674809970-89073c530b0a?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Landing() {
  const { t } = useLang();

  const features = [
    { icon: Sparkles, title: t.landing.feature1Title, desc: t.landing.feature1Desc },
    { icon: Brain, title: t.landing.feature2Title, desc: t.landing.feature2Desc },
    { icon: Activity, title: t.landing.feature3Title, desc: t.landing.feature3Desc },
    { icon: Dumbbell, title: t.landing.feature4Title, desc: t.landing.feature4Desc },
    { icon: GitCompare, title: t.landing.feature5Title, desc: t.landing.feature5Desc },
  ];

  return (
    <div className="relative overflow-hidden" data-testid="landing-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="stadium" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#090C10]/40 via-[#090C10]/70 to-[#090C10]" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#1E2638] bg-[#121721]/70 px-3 py-1 mb-6" data-testid="landing-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00F076] animate-pulse" />
              <span className="text-[10px] font-mono-tech tracking-[0.25em] text-emerald-300">{t.landing.eyebrow}</span>
            </div>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight" data-testid="landing-title">
              {t.landing.title1} <br />
              <span className="text-[#00F076]">{t.landing.title2}</span> <br />
              <span className="text-slate-300">{t.landing.title3}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              {t.landing.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=register">
                <Button size="lg" data-testid="landing-cta-primary" className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider glow-pitch">
                  {t.landing.ctaPrimary} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" data-testid="landing-cta-secondary" className="border-[#1E2638] bg-transparent hover:bg-[#121721] text-white uppercase tracking-wider">
                  {t.landing.ctaSecondary}
                </Button>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {["+38%", "24/7", "5min"].map((v, i) => (
                <div key={i} className="rounded-lg border border-[#1E2638] bg-[#121721]/70 p-3">
                  <div className="text-2xl font-display font-black text-[#00F076]">{v}</div>
                  <div className="text-[10px] font-mono-tech tracking-wider text-slate-400 uppercase">
                    {["Sprint", "AI Coach", "Setup"][i]}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative rounded-2xl overflow-hidden border border-[#1E2638] shadow-2xl">
              <img src={IMG2} alt="footballer" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090C10] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 rounded-lg backdrop-blur-md bg-[#090C10]/70 border border-[#1E2638] p-4">
                <div className="text-[10px] font-mono-tech tracking-[0.25em] text-emerald-400">AI TWIN · V.3</div>
                <div className="mt-1 font-display font-bold text-lg">Kinetic Pose Render</div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div><div className="text-slate-400 font-mono-tech">SPD</div><div className="text-white font-bold">33.2 km/h</div></div>
                  <div><div className="text-slate-400 font-mono-tech">VO2</div><div className="text-white font-bold">58.4</div></div>
                  <div><div className="text-slate-400 font-mono-tech">PWR</div><div className="text-[#00F076] font-bold">A+</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl mb-12" data-testid="landing-features-title">
            One app. <span className="text-[#00F076]">Every angle</span> of your evolution.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div
                key={i}
                data-testid={`landing-feature-${i}`}
                className="relative rounded-xl border border-[#1E2638] bg-[#121721] p-6 hover:border-[#00F076]/50 transition-all hover:-translate-y-1"
              >
                <div className="w-11 h-11 rounded-lg bg-[#00F076]/10 border border-[#00F076]/30 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-[#00F076]" />
                </div>
                <div className="font-display font-bold text-xl uppercase tracking-tight">{f.title}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 border-t border-[#1E2638]">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl mb-12">{t.landing.howTitle}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[t.landing.how1, t.landing.how2, t.landing.how3].map((step, i) => (
              <div key={i} className="rounded-xl border border-[#1E2638] bg-[#121721] p-6" data-testid={`landing-how-${i}`}>
                <div className="font-display font-black text-6xl text-[#00F076]">0{i + 1}</div>
                <div className="mt-3 text-lg text-slate-200">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1E2638] py-10 text-center text-slate-500 text-sm">
        {t.landing.footer}
      </footer>
    </div>
  );
}
